'use client';

import { DeleteOutlined as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { IconButton, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
	DataGridPro,
	GridPaginationModel,
	GridToolbarContainer,
	GridToolbarExport,
	getGridDateOperators,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import { adaptGridValueCallback, GridToolbarSlot, gridToolbarProps } from '@parkchirp/ui-library';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts';
import { DELETE_TAX_EXEMPT_CERTIFICATE_V2 } from '../../mutations';

import { GET_PRESIGNED_URL } from '../../queries';
import { SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../types';
import { dayjs, stateFieldOptions } from '../../utils';
import { Snackbar } from '../alerts';
import { AuditHistoryModal } from '../audit';
import { DatagridLinearProgress } from '../loading/datagridLinearProgress';
import { Dialog } from '../modals';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';
import { TaxExemptCertificateUploadModal } from '../modals/taxExemptCertificateUpload';

const dateOperators = getGridDateOperators();

type SubTaxExemptTax = {
	certificateId: string;
	effectiveStartDate: string;
	taxNumber: number;
	parkingPeriodYearExpiration: number;
	parkingPeriodMonthExpiration: number;
	taxExemptCertificate: {
		organizationType: string;
		issueState: string;
		companyName: string;
		exemptionNumber: string;
		filePath: string;
		expirationDate: string;
	};
};
type TaxExemptHistoryTableProps = {
	data?: SubTaxExemptTax[];
	taxTableTaxes?: { taxNumber: number; name: string }[];
	accountId: string;
	subscriptionId: string;
	facilityName: string;
	facilityNumber: string;
	timezone: string;
	organizationId: number;
	subscriptionName: string;
	facilityId: string;
	taxExempt: string;
	payorId: string;
	status: string;
	refetch: () => void;
	showDialog?: ReactNode | null;
	setShowDialog?: SetShowDialog;
	showSnackbar?: Partial<SnackbarParams>;
	setShowSnackbar?: SetShowSnackbar;
};

const CustomToolbar = ({
	accountId,
	subscriptionId,
	facilityName,
	facilityNumber,
	subscriptionName,
	facilityId,
	setShowSnackbar,
	setShowDialog,
}) => (
	<GridToolbarContainer>
		<GridToolbarExport
			csvOptions={{ fileName: 'Tax Exempt Certificate History' }}
			printOptions={{ disableToolbarButton: true }}
		/>
		<Button
			color='primary'
			onClick={() =>
				setShowDialog(
					<TaxExemptCertificateUploadModal
						singleSubscriptionData={{
							accountId,
							subscriptionId,
							facilityName,
							facilityNumber,
							subscriptionName,
							facilityId,
						}}
						setShowSnackbar={setShowSnackbar}
						setShowDialog={setShowDialog}
					/>,
				)
			}
			data-cy='subscription-info-tax-exempt-cert-upload-button'
			sx={{ borderRadius: 0 }}
			style={{ borderRadius: '3px' }}
		>
			Upload Tax Exempt Certificate
		</Button>
	</GridToolbarContainer>
);

export const TaxExemptHistoryTable: FC<TaxExemptHistoryTableProps> = ({
	data,
	taxTableTaxes,
	accountId,
	subscriptionId,
	facilityName,
	facilityNumber,
	timezone,
	organizationId,
	subscriptionName,
	facilityId,
	taxExempt,
	payorId,
	status,
	refetch,
	showSnackbar: showSnackbarDefault = null,
	setShowSnackbar: setShowSnackbarDefault = null,
	showDialog: showDialogDefault = null,
	setShowDialog: setShowDialogDefault = null,
}) => {
	const { hasComponentAccess } = useContext(CommonContext);
	const [{ pageSize, page }] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rows, setRows] = useState([]);
	const [showSnackbar, setShowSnackbar = setShowSnackbarDefault] = useState(showSnackbarDefault);
	const [showDialog, setShowDialog = setShowDialogDefault] = useState(showDialogDefault);
	const [deleteTaxExemptCert] = useMutation(DELETE_TAX_EXEMPT_CERTIFICATE_V2);

	useEffect(() => {
		if (data) {
			const grouped = data.reduce((acc, item) => {
				const { certificateId } = item;

				if (!acc[certificateId]) {
					acc[certificateId] = {
						id: certificateId,
						certificateId,
						effectiveStartDate: dayjs.utc(item.effectiveStartDate, 'x').format('MM/DD/YYYY'),
						organizationType: item.taxExemptCertificate.organizationType,
						issueState: item.taxExemptCertificate.issueState,
						companyName: item.taxExemptCertificate.companyName,
						exemptionNumber: item.taxExemptCertificate.exemptionNumber,
						filePath: item.taxExemptCertificate.filePath,
						expirationDate: dayjs.utc(item.taxExemptCertificate.expirationDate).format('MM/DD/YYYY'),
						taxNames: [],
					};
				}

				const taxEntry = taxTableTaxes.find((t) => t.taxNumber === item.taxNumber);
				if (taxEntry) {
					acc[certificateId].taxNames.push(taxEntry.name);
				}

				return acc;
			}, {});
			setRows(Object.values(grouped));
		}
	}, [data, taxTableTaxes]);

	const [getPresignedUrl] = useLazyQuery(GET_PRESIGNED_URL, {
		fetchPolicy: 'network-only',
	});

	const handleDeleteCert = async (certId, facilityId, subscriptionId) => {
		try {
			await deleteTaxExemptCert({
				variables: {
					certId,
					facilityId,
					subscriptionId,
				},
				context: {
					appName: process.env.NEXT_PUBLIC_APP_NAME,
					apiVersion: 'v2',
				},
			});
			setShowSnackbar({
				message: 'Successfully deleted this certificate',
				severity: 'success',
			});
			setShowDialog(null);
			refetch();
		} catch {
			setShowSnackbar({
				message: 'Could not delete this certificate',
				severity: 'error',
			});
		}
	};

	const handleClick = async (filePath) => {
		try {
			const response = await getPresignedUrl({
				variables: { bucket: 'parkchirp-files', s3Key: filePath, addEnv: true },
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});

			const { presignedUrl } = response.data.getPresignedUrl;
			window.location.href = presignedUrl;
		} catch (err) {
			console.error(err);
		}
	};

	const ActionWrapper = ({ children }) => (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				width: '100%',
			}}
		>
			{children}
		</div>
	);
	const handleAuditHistory = async (id: string) => {
		const auditDialog = (
			<AuditHistoryModal
				title='Tax Exempt Certificate Audit History'
				resourceId={id}
				resourceType={ResourceTypes.TAX_EXEMPT_CERTIFICATE}
				onClose={() => {
					setShowDialog(null);
				}}
			/>
		);

		setShowDialog(auditDialog);
	};

	const columns = [
		{
			field: 'Actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				return (
					<ActionWrapper>
						<EditIcon
							onClick={() =>
								setShowDialog(
									<TaxExemptCertificateUploadModal
										singleSubscriptionData={{
											id: params.row.certificateId,
											facilityId: facilityId,
											facilityNumber: facilityNumber,
											facilityName: facilityName,
											accountId: `AC-${accountId}`,
											subId: payorId,
											subscriptionName: subscriptionName,
											subStatus: status,
											certId: params.row.certificateId,
											issueState: params.row.issueState,
											taxExempt: taxExempt,
											subscriptionId: subscriptionId,
											companyName: params.row.companyName,
											issueDate: dayjs.utc(params.row.issueDate).format('MM/DD/YYYY'),
											filePath: params.row.filePath,
											expirationDate: dayjs.utc(params.row.expirationDate).format('MM/DD/YYYY'),
											exemptionNumber: params.row.exemptionNumber,
											organizationType: params.row.organizationType,
											organizationId: organizationId,
											createdAt: dayjs.utc(params.row.createdAt).format('MM/DD/YYYY'),
											timezone: timezone,
											subscriptionTaxExemptTaxes: data,
										}}
										setShowSnackbar={setShowSnackbar}
										setShowDialog={setShowDialog}
										refetch={refetch}
									/>,
								)
							}
						/>
						{hasComponentAccess('AuditHistoryTable') && (
							<IconButton
								onClick={() => {
									handleAuditHistory(params.row.id);
								}}
							>
								<Tooltip title='View Audit History'>
									<HistoryIcon />
								</Tooltip>
							</IconButton>
						)}
						<DeleteIcon
							onClick={() => {
								const cancelFunction = () => {
									setShowDialog(null);
								};
								const confirmationInputModal = (
									<ConfirmationInputModal
										title={`Are you sure you want to delete the certificate for AC-${accountId}?`}
										validationString='DELETE'
										dismissText='Cancel'
										confirmText='Confirm'
										handleDismiss={cancelFunction}
										handleConfirm={() =>
											handleDeleteCert(params.row.id, facilityId, subscriptionId)
										}
									/>
								);
								setShowDialog(confirmationInputModal);
							}}
						/>
					</ActionWrapper>
				);
			},
		},
		{
			field: 'certificate',
			headerName: 'Certificate',
			filterable: false,
			renderCell: (params) => {
				if (params.row.filePath) {
					return <InsertDriveFileIcon onClick={() => handleClick(params.row.filePath)} />;
				}
				return null;
			},
		},
		{
			field: 'effectiveStartDate',
			headerName: 'Start Date',
			width: 150,
			filterOperators: dateOperators,
			filterable: false,
			sortable: false,
		},
		{
			field: 'expirationDate',
			headerName: 'Expiration Date',
			width: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'organizationType',
			headerName: 'Organization Type',
			width: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'issueState',
			headerName: 'Issue State',
			width: 100,
			type: 'singleSelect' as const,
			valueOptions: stateFieldOptions,
			valueGetter: adaptGridValueCallback((params) =>
				params.row.issueState !== undefined ? params.row.issueState : 'Alabama',
			),
			filterable: false,
			sortable: false,
		},

		{
			field: 'exemptionNumber',
			headerName: 'Exemption Number',
			width: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'companyName',
			headerName: 'Company Name',
			width: 150,
			filterable: false,
			sortable: false,
		},
		{
			field: 'taxNames',
			renderCell: (params) => params.row.taxNames.join(', '),
			headerName: 'Tax Exempt Taxes',
			width: 500,
			filterable: false,
			sortable: false,
		},
	];

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='tax-exempt-certificates-datagrid-container'>
			<Typography variant='h6' gutterBottom marginTop='10px'>
				Tax Exempt Certificate History
			</Typography>
			<DataGridPro
				sx={{
					'& .MuiDataGrid-columnHeaderTitle': {
						textOverflow: 'clip',
						whiteSpace: 'break-spaces',
						lineHeight: 'normal',
					},
					'& .MuiBadge-badge': {
						display: 'none',
					},
					height: '100%',
				}}
				slots={{
					loadingOverlay: DatagridLinearProgress,
					toolbar: GridToolbarSlot,
				}}
				slotProps={{
					toolbar: gridToolbarProps(CustomToolbar, {
						accountId,
						subscriptionId,
						facilityName,
						facilityNumber,
						subscriptionName,
						facilityId,
						setShowSnackbar,
						setShowDialog,
					}),
				}}
				rows={rows || []}
				editMode='row'
				columns={columns}
				rowCount={rows.length}
				paginationModel={{ page, pageSize }}
				pageSizeOptions={[pageSize]}
				hideFooter={false}
				pagination
				paginationMode='server'
				sortingMode='server'
			/>
			{!showSnackbarDefault && <Snackbar showSnackbar={showSnackbar} setShowSnackbar={setShowSnackbar} />}
			{!showDialogDefault && <Dialog showDialog={showDialog} setShowDialog={setShowDialog} />}
		</div>
	);
};
